import axios from 'axios';
import { store, persistor } from '../store/store';
import { logout } from '../store/authSlice';
import { clearAssociadoData } from '../store/associadoSlice';
import { resetCodigoData } from '../store/codigoSlice';
import { clearCotacaoData } from '../store/cotacaoSlice';
import { resetRespostasDoSga } from '../store/respostasDoSgaSlice';
import { resetInspection } from '../store/vistoriaSlice';
import { resetChecklistAcessorios } from '../store/checklistAcessoriosSlice';
import { gravarAvisoSessao, MotivoLogout } from './authAviso';

let loggingOut = false;

/**
 * Logout unificado, utilizável fora de componentes React (interceptor HTTP,
 * bootstrap do App) e dentro deles (botão "Sair"). Limpa o estado local,
 * tenta invalidar a sessão no servidor (best-effort) e redireciona para /login.
 *
 * Usa `window.location.assign` (e não o router) porque pode ser chamado a
 * partir do interceptor, que roda fora do contexto do React Router.
 *
 * `motivo` (at-22): quando o logout é AUTOMÁTICO, informar o motivo — ele é
 * gravado em `sessionStorage` e exibido na tela de login depois do redirect.
 * Antes daqui saía um `toast.info()`, que morria no reload logo abaixo e deixava
 * TODO logout automático silencioso. Logout manual (botão "Sair") chama sem
 * motivo: o usuário sabe o que fez.
 */
export async function performLogout(motivo?: MotivoLogout): Promise<void> {
  // Evita múltiplos logouts concorrentes (ex.: várias respostas 401 no mesmo instante).
  if (loggingOut) return;
  loggingOut = true;

  // Antes de qualquer await: se o redirect acontecer por outro caminho, o aviso
  // já está gravado.
  if (motivo) gravarAvisoSessao(motivo);

  try {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/logout`,
      {},
      { withCredentials: true },
    );
  } catch {
    // best-effort: sessão pode já estar inválida no servidor
  }

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('firstLogin');

  store.dispatch(logout());
  store.dispatch(clearAssociadoData());
  store.dispatch(resetCodigoData());
  store.dispatch(clearCotacaoData());
  store.dispatch(resetRespostasDoSga());
  // at-24: o slice de vistoria e o checklist NÃO eram limpos aqui. O estado sobrevivia
  // ao logout e podia apontar para uma vistoria que já não existe (ou já foi
  // descartada), sem nenhuma tela para retomá-lo. A retomada agora vem do BANCO, pelo
  // painel de `/vistoria` — fonte única, que funciona em outro dispositivo também.
  store.dispatch(resetInspection());
  store.dispatch(resetChecklistAcessorios());

  // Garante que o estado limpo seja persistido ANTES do reload — sem isso a
  // gravação (throttled) do redux-persist pode não ocorrer antes do redirect,
  // fazendo a página recarregada reler o token expirado de `persist:root` e
  // cair em loop de logout numa sessão realmente expirada.
  try {
    await persistor.flush();
  } catch {
    // se o flush falhar, ainda assim redireciona
  }

  window.location.assign('/login');
}
